/**
 * Drivers Map Page
 * صفحة خريطة السائقين
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantAuth } from '../../context/TenantAuthContext';
import { useToast } from '../../context/ToastContext';
import '../shared/PageStyles.css';

const I = {
  back: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>,
  refresh: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>,
  map: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="10" r="3"/><path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 7 8 11.7z"/></svg>,
  phone: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  truck: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
};

const DriversMapPage = () => {
  const navigate = useNavigate();
  const { apiCall } = useTenantAuth();
  const { error: showError } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [filter, setFilter] = useState('all');
  const [lastUpdate, setLastUpdate] = useState(null);

  const loadDriversLocations = useCallback(async () => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const data = await apiCall(`/tenant/drivers/locations/all${params}`);
      setDrivers(data || []);
      setLastUpdate(new Date());
    } catch (err) {
      showError('فشل تحميل مواقع السائقين');
    } finally {
      setLoading(false);
    }
  }, [apiCall, filter, showError]);

  useEffect(() => { loadDriversLocations(); }, [loadDriversLocations]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(loadDriversLocations, 30000);
    return () => clearInterval(interval);
  }, [loadDriversLocations]);

  const driversWithLocation = drivers.filter(d => d.location?.latitude);
  const driversWithoutLocation = drivers.filter(d => !d.location?.latitude);

  const getStatusColor = (status, isOnline) => {
    if (status === 'active' && isOnline) return '#22c55e';
    if (status === 'active') return '#3b82f6';
    if (status === 'suspended') return '#f59e0b';
    return '#6b7280';
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header__title">
          <button className="btn btn--ghost" onClick={() => navigate('/tenant/drivers')} style={{ marginLeft: '-8px', marginBottom: '8px' }}>
            {I.back} <span>العودة للسواقين</span>
          </button>
          <h1>خريطة السائقين</h1>
          <p>{driversWithLocation.length} سائق بموقع معروف من أصل {drivers.length}</p>
        </div>
        <div className="header-actions">
          <select value={filter} onChange={e => setFilter(e.target.value)} className="filter-select">
            <option value="all">جميع السائقين</option>
            <option value="active">النشطين فقط</option>
            <option value="suspended">المعلقين</option>
          </select>
          <button className="btn btn--secondary" onClick={loadDriversLocations}>
            {I.refresh} تحديث
          </button>
        </div>
      </div>

      {lastUpdate && (
        <div className="last-update">
          آخر تحديث: {lastUpdate.toLocaleTimeString('ar-SA')}
        </div>
      )}

      {loading ? (
        <div className="page-loading"><div className="spinner" /></div>
      ) : (
        <div className="map-layout">
          {/* Drivers List */}
          <div className="drivers-sidebar">
            <h3>السائقين ({drivers.length})</h3>
            
            {driversWithLocation.length > 0 && (
              <div className="drivers-group">
                <h4 className="group-title">{I.map} بموقع معروف ({driversWithLocation.length})</h4>
                {driversWithLocation.map(driver => (
                  <div 
                    key={driver.driver_id} 
                    className={`driver-item ${selectedDriver?.driver_id === driver.driver_id ? 'driver-item--selected' : ''}`}
                    onClick={() => setSelectedDriver(driver)}
                  >
                    <div className="driver-status-dot" style={{ background: getStatusColor(driver.status, driver.is_online) }} />
                    <div className="driver-item-info">
                      <span className="driver-name">{driver.full_name}</span>
                      <span className="driver-meta">
                        {driver.active_orders > 0 && <span className="active-orders">{driver.active_orders} طلب</span>}
                        {driver.location?.speed && <span>{driver.location.speed.toFixed(0)} كم/س</span>}
                      </span>
                    </div>
                    <a 
                      href={`https://www.google.com/maps?q=${driver.location.latitude},${driver.location.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="map-link"
                      onClick={e => e.stopPropagation()}
                    >
                      {I.map}
                    </a>
                  </div>
                ))}
              </div>
            )}

            {driversWithoutLocation.length > 0 && (
              <div className="drivers-group drivers-group--muted">
                <h4 className="group-title">بدون موقع ({driversWithoutLocation.length})</h4>
                {driversWithoutLocation.map(driver => (
                  <div key={driver.driver_id} className="driver-item driver-item--muted">
                    <div className="driver-status-dot" style={{ background: '#d1d5db' }} />
                    <div className="driver-item-info">
                      <span className="driver-name">{driver.full_name}</span>
                      <span className="driver-meta">{driver.is_online ? 'متصل' : 'غير متصل'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Map Area */}
          <div className="map-container">
            {selectedDriver ? (
              <div className="selected-driver-card">
                <div className="card-header">
                  <div className="driver-avatar">{selectedDriver.full_name?.charAt(0)}</div>
                  <div className="driver-details">
                    <h3>{selectedDriver.full_name}</h3>
                    <p dir="ltr">{selectedDriver.phone}</p>
                  </div>
                  <span className={`status-badge status-badge--${selectedDriver.is_online ? 'online' : 'offline'}`}>
                    {selectedDriver.is_online ? 'متصل' : 'غير متصل'}
                  </span>
                </div>
                
                {selectedDriver.location && (
                  <div className="location-details">
                    <div className="location-row">
                      <span>خط العرض:</span>
                      <span>{selectedDriver.location.latitude?.toFixed(6)}</span>
                    </div>
                    <div className="location-row">
                      <span>خط الطول:</span>
                      <span>{selectedDriver.location.longitude?.toFixed(6)}</span>
                    </div>
                    {selectedDriver.location.speed && (
                      <div className="location-row">
                        <span>السرعة:</span>
                        <span>{selectedDriver.location.speed.toFixed(1)} كم/س</span>
                      </div>
                    )}
                    <div className="location-row">
                      <span>آخر تحديث:</span>
                      <span>{selectedDriver.location.recorded_at ? new Date(selectedDriver.location.recorded_at).toLocaleString('ar-SA') : '-'}</span>
                    </div>
                  </div>
                )}

                <div className="card-actions">
                  <a 
                    href={`https://www.google.com/maps?q=${selectedDriver.location?.latitude},${selectedDriver.location?.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn--primary"
                  >
                    {I.map} فتح في Google Maps
                  </a>
                  <button className="btn btn--secondary" onClick={() => navigate(`/tenant/drivers/${selectedDriver.driver_id}`)}>
                    عرض الملف الشخصي
                  </button>
                </div>
              </div>
            ) : (
              <div className="map-placeholder">
                <div className="placeholder-icon">{I.map}</div>
                <h3>اختر سائق لعرض موقعه</h3>
                <p>اضغط على أي سائق من القائمة لعرض تفاصيل موقعه</p>
              </div>
            )}

            {/* Quick Stats */}
            <div className="map-stats">
              <div className="stat-item">
                <span className="stat-dot" style={{ background: '#22c55e' }} />
                <span>متصل: {drivers.filter(d => d.is_online).length}</span>
              </div>
              <div className="stat-item">
                <span className="stat-dot" style={{ background: '#3b82f6' }} />
                <span>نشط: {drivers.filter(d => d.status === 'active' && !d.is_online).length}</span>
              </div>
              <div className="stat-item">
                <span className="stat-dot" style={{ background: '#f59e0b' }} />
                <span>في توصيل: {drivers.filter(d => d.active_orders > 0).length}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .header-actions {
          display: flex;
          gap: 0.75rem;
        }
        .last-update {
          font-size: 0.85rem;
          color: var(--text-secondary);
          margin-bottom: 1rem;
        }
        .map-layout {
          display: grid;
          grid-template-columns: 320px 1fr;
          gap: 1.5rem;
          height: calc(100vh - 220px);
          min-height: 500px;
        }
        .drivers-sidebar {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 1rem;
          overflow-y: auto;
        }
        .drivers-sidebar h3 {
          margin: 0 0 1rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid var(--border);
        }
        .drivers-group {
          margin-bottom: 1.5rem;
        }
        .drivers-group--muted {
          opacity: 0.7;
        }
        .group-title {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.85rem;
          color: var(--text-secondary);
          margin: 0 0 0.75rem;
        }
        .group-title svg {
          width: 16px;
          height: 16px;
        }
        .driver-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem;
          border-radius: 8px;
          cursor: pointer;
          transition: background 0.2s;
        }
        .driver-item:hover {
          background: var(--background);
        }
        .driver-item--selected {
          background: var(--primary-light, #eff6ff);
          border: 1px solid var(--primary);
        }
        .driver-item--muted {
          cursor: default;
        }
        .driver-status-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .driver-item-info {
          flex: 1;
          min-width: 0;
        }
        .driver-name {
          display: block;
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .driver-meta {
          display: flex;
          gap: 0.5rem;
          font-size: 0.8rem;
          color: var(--text-secondary);
        }
        .active-orders {
          color: var(--primary);
          font-weight: 500;
        }
        .map-link {
          padding: 0.5rem;
          border-radius: 6px;
          color: var(--primary);
          transition: background 0.2s;
        }
        .map-link:hover {
          background: var(--background);
        }
        .map-link svg {
          width: 18px;
          height: 18px;
        }
        .map-container {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
        }
        .map-placeholder {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          color: var(--text-secondary);
        }
        .placeholder-icon {
          width: 80px;
          height: 80px;
          background: var(--background);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1rem;
        }
        .placeholder-icon svg {
          width: 40px;
          height: 40px;
        }
        .selected-driver-card {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .card-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid var(--border);
          margin-bottom: 1rem;
        }
        .driver-avatar {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--primary), #60a5fa);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          font-weight: 600;
        }
        .driver-details h3 {
          margin: 0 0 4px;
        }
        .driver-details p {
          margin: 0;
          color: var(--text-secondary);
        }
        .status-badge {
          padding: 0.35rem 0.75rem;
          border-radius: 20px;
          font-size: 0.8rem;
          margin-right: auto;
        }
        .status-badge--online {
          background: #dcfce7;
          color: #166534;
        }
        .status-badge--offline {
          background: #f3f4f6;
          color: #6b7280;
        }
        .location-details {
          background: var(--background);
          border-radius: 10px;
          padding: 1rem;
          margin-bottom: 1rem;
        }
        .location-row {
          display: flex;
          justify-content: space-between;
          padding: 0.5rem 0;
          border-bottom: 1px solid var(--border);
        }
        .location-row:last-child {
          border-bottom: none;
        }
        .card-actions {
          display: flex;
          gap: 0.75rem;
          margin-top: auto;
        }
        .card-actions .btn {
          flex: 1;
        }
        .map-stats {
          display: flex;
          gap: 1.5rem;
          padding-top: 1rem;
          border-top: 1px solid var(--border);
          margin-top: auto;
        }
        .stat-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
        }
        .stat-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }
        @media (max-width: 900px) {
          .map-layout {
            grid-template-columns: 1fr;
            height: auto;
          }
          .drivers-sidebar {
            max-height: 300px;
          }
          .map-container {
            min-height: 400px;
          }
        }
      `}</style>
    </div>
  );
};

export default DriversMapPage;
